import { useEffect, useState, useRef } from "react";
import Icon1 from "../../../assets/icons/Icon";
import type { Attestation, File, RTB_RE20 } from "../../../types/basicTypes";
import { Org, User } from "../../../types/basicTypes";
import JSZip, { folder } from "jszip";
import { useAppContext } from "../../../config/AppContext";
import { getOrRefreshFirebaseToken } from "../../../config/getFirebaseToken";
import uploadFiles from "../../../api/upload/uploadFiles";
import { Spin } from "antd";
import extractInfosFromVMC from "../../../api/ai/extractInfosFromVMC";
import extractLogement from "../../../api/ai/extractLogements";
import { getLogementFromSimpleType } from "../../../sample/generalData";
import deleteDocApi from "../../../api/database/deleteDocApi";
import updateDocApi from "../../../api/database/updateDocApi";
import {
  getDocDataById,
  getFlutterTime,
  getRefFromId,
} from "../../../models/DataFunctions";
import Space from "../../../layout/Space";
import CheckBoxGrey from "../../../textfields/CheckBoxGrey";
import SecondaryButton from "../../../buttons/SecondaryButton";
import PrimaryButton from "../../../buttons/PrimaryButton";
import SmallSpinner from "../../../loading/SmallSpinner";
import extractXml from "../../../api/xml/extractXml";
import RTBRE20Modal from "../../../modals/RTBRE20Modal";
import SexySpin from "../../../assets/SexySpinner";
import signRE2020 from "../../../api/sign/signRE2020";

interface NewAttestationModalProps {
  userData: User; // Replace 'any' with a more specific type if available
  orgData: Org; // Replace 'any' with a more specific type if available

  navigateWorkspace: (path: string) => void;
  handleClickVariant: (
    message: string,
    variant: "default" | "error" | "success" | "warning" | "info" | undefined
  ) => void; // Optional function to handle notifications
  setOpen: (open: boolean) => void; // Optional function to control the visibility of the initialize modal
  refreshData: () => void; // Function to refresh data after updating the attestation
  rtb_compatible?: boolean; // Optional prop to indicate if the XML is RTB compatible, default to true
  extractCarbon: (xml: any, insulator_carbon?: boolean) => Promise<void>; // Optional function to extract carbon data
  generateRTBAttestation: (xml: any, rtb: RTB_RE20 | null) => Promise<void>; // Optional function to generate RTB attestation
  generateCarbonAttestation: (xml: any, selectedFields: any[]) => Promise<void>; // Optional function to
  generateInsulatorAttestation: (xml: any) => Promise<void>; // Optional function to generate insulator attestation
  generateInsulatorCarbonAttestation: (
    xml: any,
    selectedFields: any[]
  ) => Promise<void>; // Optional function to generate insulator carbon attestation
  generateComplexModelAttestation: (
    xml: any,
    type: "insulator" | "carbon" | "insulator_carbon"
  ) => Promise<void>; // Optional function to generate complex model attestation
  extractStep: "carbon" | "insulator_carbon" | null; // Optional step to indicate the extraction process
  setExtractStep: (step: "carbon" | "insulator_carbon" | null) => void; // Optional function to set the extraction step
  carbonBatch: any[]; // Optional prop to pass carbon batch data
  xmlId: string; // Optional prop to pass XML ID
  newAttestation?: Attestation; // Optional prop to pass a new attestation object
}
const NewAttestationModal = ({
  userData,
  orgData,
  navigateWorkspace,
  rtb_compatible = true, // Default to true if not provided
  setOpen,
  handleClickVariant,
  refreshData,
  extractCarbon,
  generateRTBAttestation,
  generateCarbonAttestation,
  generateComplexModelAttestation,
  generateInsulatorAttestation,
  generateInsulatorCarbonAttestation,
  extractStep = null,
  setExtractStep = () => {}, // Default to a no-op function if not provided
  carbonBatch = [],
  xmlId = "", // Default to an empty string if not provided
  newAttestation, // Default to null if not provided
}: NewAttestationModalProps) => {
  const [initialize, setInitializeStep] = useState<{
    step: string;
    data: any;
  }>({
    step: "type",
    data: null,
  });
  const [loadingSignature, setLoadingSignature] = useState(false);
  const [loadingFile, setLoadingFile] = useState(false);
  const { appState, setAppState, filter } = useAppContext();
  const prefix = appState.dev ? appState.dev_url : appState.prod_url;
  const [extractingData, setExtractingData] = useState(false);

  const [secondsPassed, setSecondsPassed] = useState(0);
  // Fonction utilitaire pour aplatir un objet en un tableau de champs extraits
  const [delay, setDelay] = useState(20);
  const [step, setStep] = useState("type");
  const [selectedFields, setSelectedFields] = useState<string[]>([]);
  const [isExtracted, setIsExtracted] = useState(false);
  const [xml, setXml] = useState<any>(null);
  const [rtb_re20, setRTB_RE20] = useState<RTB_RE20 | null>(null);
  const [generatedUrl, setGeneratedUrl] = useState<string | null>(null);

  // Fonction pour aplatir récursivement les objets imbriqués

  useEffect(() => {
    const fetchXmlData = async () => {
      if (xmlId) {
        try {
          const xmlData = await getDocDataById("xml", xmlId);
          if (xmlData) {
            setXml(xmlData);
            setRTB_RE20(orgData?.config?.atf?.rtb_re2020 || null);
          } else {
            handleClickVariant("Aucun fichier XML trouvé", "error");
          }
        } catch (error) {
          console.error(
            "Erreur lors de la récupération du fichier XML:",
            error
          );
          handleClickVariant(
            "Erreur lors de la récupération du fichier XML",
            "error"
          );
        }
      }
    };
    fetchXmlData();
  }, [xmlId]);
  useEffect(() => {
    // seconds passed since the extraction started
    if (extractingData && secondsPassed < 60) {
      const interval = setInterval(() => {
        setSecondsPassed((prev) => prev + 1);
      }, 1000);
      return () => clearInterval(interval);
    } else {
      setSecondsPassed(0);
    }
  }, [extractingData]);

  const handleCheck = (field: any) => {
    if (selectedFields.includes(field.X)) {
      setSelectedFields(selectedFields.filter((f) => f !== field.X));
    } else {
      setSelectedFields([...selectedFields, field.X]);
    }
  };

  const handleComplexModelAttestation = async (
    type: "insulator" | "carbon" | "insulator_carbon"
  ) => {
    setExtractingData(true);
    if (!xml) {
      handleClickVariant("Aucun fichier XML trouvé", "error");
      setExtractingData(false);
      setOpen(false);
      return;
    }
    try {
      await generateComplexModelAttestation(xml, type);
      setExtractingData(false);
      setInitializeStep({
        ...initialize,
        step: "url",
      });
    } catch (error) {
      console.error(
        "Erreur lors de la génération de l'attestation complexe",
        error
      );
      handleClickVariant(
        "Erreur lors de la génération de l'attestation complexe",
        "error"
      );
      setExtractingData(false);
      return
    }
  };

  const handleGenerateRTBAttestation = async (
    type: string,
    rtb_re20: RTB_RE20
  ) => {
    setExtractingData(true);
    if (!xml) {
      handleClickVariant("Aucun fichier XML trouvé", "error");
      setExtractingData(false);
      setOpen(false);
      return;
    }
    try {
      switch (type) {
        case "rtb_re2020":
          await generateRTBAttestation(xml, rtb_re20);
          break;
        case "rtb_rt2012":
          console.log("not implemented yet");
      }
      setExtractingData(false);
      setInitializeStep({
        ...initialize,
        step: "url",
      });
    } catch (error) {
      console.error("Erreur lors de la génération de l'attestation RTB", error);
      handleClickVariant(
        "Erreur lors de la génération de l'attestation RTB",
        "error"
      );
      setExtractingData(false);
      return;
    }

  };

  const handleExtractXmlATH = async (type: string) => {
    setExtractingData(true);
    if (!xml) {
      handleClickVariant("Aucun fichier XML trouvé", "error");
      setExtractingData(false);

      setOpen(false);
      return;
    }
    try {
      if (!isExtracted) {
        switch (type) {
          case "insulator":
            await generateInsulatorAttestation(xml);
            break;
          case "carbon":
            await extractCarbon(xml);
            setExtractStep("carbon");
            break;
          case "insulator_carbon":
            await extractCarbon(xml, true);
            setExtractStep("insulator_carbon");
            break;
        }
      } else {
        switch (type) {
          case "carbon":
            await generateCarbonAttestation(xml, selectedFields);
            setExtractStep(null);
            setInitializeStep({
              ...initialize,
              step: "url",
            });

            break;
          case "insulator_carbon":
            await generateInsulatorCarbonAttestation(xml, selectedFields);
            setExtractStep(null);
            setInitializeStep({
              ...initialize,
              step: "url",
            });

            break;
        }
      }
      setIsExtracted(true);
      handleClickVariant("Extraction réussie", "success");
      if (type === "insulator") {
        handleClickVariant(
          "Attestation isolants générée avec succès",
          "success"
        );
        setInitializeStep({
          ...initialize,
          step: "url",
        });
      }
    } catch (error) {
      console.error("Erreur lors de l'extraction", error);
      handleClickVariant("Erreur lors de l'extraction", "error");
    } finally {
      if (orgData.config?.ath?.model === "complex") {
        setInitializeStep({
          ...initialize,
          step: "url",
        });
        setExtractStep(null);
      }
      setExtractingData(false);
    }
  };

  const handleDeleteFolder = async () => {
    setOpen(false);
    refreshData();
    navigateWorkspace("/xmls");
  };

  const pdfIframeRef = useRef<HTMLIFrameElement>(null);

  const handleSignature = async () => {
    try{
      setLoadingSignature(true)
      let width = 0;
      let height = 0;
      if (pdfIframeRef.current) {
        width = pdfIframeRef.current.offsetWidth;
        height = pdfIframeRef.current.offsetHeight;
        console.log("PDF iframe width:", width, "height:", height);
      }
      const fields = [{type: "Signature", name: "Signature", bb:410, pp:240, ww: 150, hh: 75, page: 6, target: "sign"},
        {type:"Date", name:"Date", bb:450, pp:210, ww:150, hh: 25, page:6}
      ]

      const token = await getOrRefreshFirebaseToken();
      let success = await signRE2020(
        fields,
        token,
        newAttestation?.id || "",
        prefix,
        height,
        width,
        newAttestation?.file?.name || "re2020.pdf",
      )
      if (!success) {
        handleClickVariant("Erreur lors de la signature de l'attestation", "error");
        return;
      }
      else {
        handleClickVariant("Attestation signée avec succès", "success");
        setLoadingSignature(false);
        refreshData();
        setOpen(false);       
      }

    } catch (error) {
        console.error("Erreur lors de la signature de l'attestation", error);
        handleClickVariant("Erreur lors de la signature de l'attestation", "error");
      }

  }

  return (
    <div className="fixed top-0 left-0 w-full h-screen flex-row gap-8 bg-neutral-50 z-10 shadow-md flex items-center justify-center">
      <div
        onClick={() => {
          handleDeleteFolder();
        }}
        className="absolute top-4 bg-white rounded-full px-4 border-1 shadow-sm cursor-pointer py-2 text-h2 "
      >
        Annuler
      </div>
      {(extractStep == "carbon" || extractStep == "insulator_carbon") && (
        <div className="flex flex-col  max-w-c900 items-center w-full">
          <div>Choisir les champs</div>
          <div className="flex flex-col w-full">
            <div className="flex flex-row gap-2 ml-1 font-semibold">
              <CheckBoxGrey
                checked={selectedFields.length === carbonBatch.length}
                onChange={() => {
                  if (selectedFields.length === carbonBatch.length) {
                    setSelectedFields([]);
                  } else {
                    setSelectedFields(carbonBatch.map((field) => field.X));
                  }
                }}
              ></CheckBoxGrey>
              <p className="text-h3 w-c100 ">Sous Lot</p>
              <p className="text-h3  w-c450 ">Nom</p>
              <p className="text-h3  w-c100">ID</p>
              <p className="text-h3  w-c50">Qt</p>
              <p className="text-h3  w-c50">Un</p>
            </div>
            <div className="flex flex-col gap-1 w-full max-h-c400 overflow-auto">
              {carbonBatch.map((field) => (
                <div
                  key={field.X}
                  className={`flex flex-row gap-2 w-full rounded-lg px-1 items-center ${
                    selectedFields.includes(field.X) ? "bg-green-100" : ""
                  }`}
                >
                  <CheckBoxGrey
                    checked={selectedFields.includes(field.X)}
                    onChange={() => handleCheck(field)}
                  ></CheckBoxGrey>

                  <span className="text-h3 w-c100">sous lot {field.num}</span>
                  <span className="text-h3  w-c450 ">
                    {field.nom?.substring(0, 65)}
                  </span>
                  <span className="text-h3  w-c100">
                    {field.id?.substring(0, 12)}
                  </span>
                  <span className="text-h3  w-c50">{field.qt}</span>
                  <span className="text-h3  w-c50">{field.un}</span>
                </div>
              ))}
            </div>
          </div>
          <Space y={15}></Space>
          <div className="flex flex-row w-full justify-end p-3 gap-2">
            <SecondaryButton
              onClick={() => {
                setExtractStep(null);
              }}
            >
              Annuler
            </SecondaryButton>
            <PrimaryButton
              onClick={() => {
                handleExtractXmlATH(extractStep);
              }}
            >
              Générer l'attestation{" "}
            </PrimaryButton>
          </div>
          <Space y={15}></Space>
        </div>
      )}
      {initialize.step === "type" && (
        <>
          <div
            onClick={() => {
              setInitializeStep({
                step: "type2",
                data: {
                  type: "insulator_carbone",
                },
              });
            }}
            className="bg-white hover:shadow-lg shadow-md border-1 cursor-pointer text-black rounded-2xl h-32 flex flex-col justify-center gap-4 items-center  w-fit  p-8"
          >
            <Icon1 icon_name="pdf" size={30} fill="black"></Icon1>
            <div className="text-h1 ">Attestation isolants & carbone</div>
          </div>
          <div
            onClick={() => {
              setInitializeStep({
                step: "type2",
                data: {
                  type: "rtb",
                },
              });
            }}
            className={`
              ${"bg-black text-white cursor-pointer hover:shadow-lg"}
                shadow-md border-1  rounded-2xl h-32 flex flex-col justify-center gap-4 items-center  w-fit  p-8`}
          >
            <Icon1 icon_name="file" size={30} fill={"white"}></Icon1>
            <div className="text-h1 ">Attestation RT batiment</div>
          </div>
        </>
      )}

      {initialize.step === "type2" &&
        initialize.data.type === "insulator_carbone" && (
          <>
            <div
              onClick={() => {
                setInitializeStep({
                  step: "xml",
                  data: {
                    type: "insulator_carbone",
                    type2: "insulator",
                  },
                });
                if (orgData.config?.ath?.model === "complex") {
                  handleComplexModelAttestation("insulator");
                } else {
                  handleExtractXmlATH("insulator");
                }
              }}
              className="bg-white hover:shadow-lg shadow-md border-1 cursor-pointer text-black rounded-2xl h-32 flex flex-col justify-center gap-4 items-center  w-fit  p-8"
            >
              <Icon1 icon_name="pdf" size={30} fill="black"></Icon1>
              <div className="text-h1 ">Attestation isolants</div>
            </div>
            <div
              onClick={() => {
                setInitializeStep({
                  step: "xml",
                  data: {
                    type: "insulator_carbone",
                    type2: "carbon",
                  },
                });
                if (orgData.config?.ath?.model === "complex") {
                  handleComplexModelAttestation("carbon");
                } else {
                  handleExtractXmlATH("carbon");
                }
              }}
              className="bg-white hover:shadow-lg shadow-md border-1 cursor-pointer text-black rounded-2xl h-32 flex flex-col justify-center gap-4 items-center  w-fit  p-8"
            >
              <Icon1 icon_name="pdf" size={30} fill="black"></Icon1>
              <div className="text-h1 ">Attestation carbone</div>
            </div>
            <div
              onClick={() => {
                setInitializeStep({
                  step: "xml",
                  data: {
                    type: "insulator_carbone",
                    type2: "both",
                  },
                });
                if (orgData.config?.ath?.model === "complex") {
                  handleComplexModelAttestation("insulator_carbon");
                } else {
                  handleExtractXmlATH("insulator_carbon");
                }
              }}
              className="bg-black hover:shadow-lg shadow-md border-1 cursor-pointer text-white rounded-2xl h-32 flex flex-col justify-center gap-4 items-center  w-fit  p-8"
            >
              <Icon1 icon_name="pdf" size={30} fill="white"></Icon1>
              <div className="text-h1 ">Attestation isolants & carbone</div>
            </div>
          </>
        )}
      {initialize.step === "type2" && initialize.data.type === "rtb" && (
        <>
          <div
            onClick={() => {
              // setInitializeStep({
              //   step: "xml",
              //   data: {
              //     type: "rtb",
              //     type2: "rt2012",
              //   },
              // });
              console.log("not implemented yet");
            }}
            className="
            bg-neutral-200 text-shadow_25 cursor-not-allowed
            shadow-md border-1  text-black rounded-2xl h-32 flex flex-col justify-center gap-4 items-center  w-fit  p-8"
          >
            <Icon1 icon_name="file" size={30} fill="rgba(0,0,0,0.25)"></Icon1>
            <div className="flex flex-col items-center">
              <div className="text-h2 ">Attestation RT2012</div>
              <div className="text-h2 text-shadow_50">Bientôt</div>
            </div>
          </div>
          <div
            onClick={() => {
              if (!rtb_compatible) {
                handleClickVariant(
                  "Ce type de fichier n'est pas compatible avec RTB",
                  "error"
                );
                return;
              }
              setInitializeStep({
                step: "xml",
                data: {
                  type: "rtb",
                  type2: "re2020",
                },
              });
            }}
            className={`
              ${
                !rtb_compatible
                  ? "bg-neutral-200 text-shadow_25 cursor-not-allowed"
                  : "bg-black text-white cursor-pointer hover:shadow-lg"
              }
                shadow-md border-1  rounded-2xl h-32 flex flex-col justify-center gap-4 items-center  w-fit  p-8`}
          >
            <Icon1
              icon_name="file"
              size={30}
              fill={!rtb_compatible ? "rgba(0,0,0,0.25)" : "white"}
            ></Icon1>
            <div className="text-h1 ">Attestation RE2020</div>
          </div>
        </>
      )}

      {initialize.step === "xml" &&
        initialize.data.type === "rtb" &&
        initialize.data.type2 === "re2020" && (
          <>
            <RTBRE20Modal
              userData={userData}
              orgData={orgData}
              xml_id={xmlId}
              onClose={() => {
                setInitializeStep({ step: "type", data: null });
              }}
              handleGenerateRTBAttestation={handleGenerateRTBAttestation}
              setExtractingData={setExtractingData}
            ></RTBRE20Modal>
          </>
        )}

      {initialize.step === "url" && newAttestation?.file?.url && (
        <div className="flex flex-col w-full max-w-c800 h-full items-center justify-center gap-4">
          <div className="bg-neutral-600 p-2 rounded-lg w-full h-[70%]">
            <iframe
              ref={pdfIframeRef}
              src={newAttestation.file.url}
              className="w-full h-full"
              title="New Attestation"
            ></iframe>
          </div>
          <div className="flex flex-row w-full items-center justify-center gap-4">
          <PrimaryButton
            onClick={() => {
              setOpen(false);
              refreshData();
            }}
          >
            Valider l'attestation
          </PrimaryButton>
          {initialize.data.type === "rtb" && initialize.data.type2 === "re2020" && (
            <SecondaryButton
              disabled={loadingSignature}
              onClick={() => {
                handleSignature();
              }}
            >
              Signer l'attestation
            </SecondaryButton>
          )}
          </div>
        </div>
      )}

      {extractingData && (
        <>
          <div
            onClick={() => {}}
            className=" fixed top-0 left-0 h-screen w-screen bg-black flex flex-col justify-center gap-4  items-center   "
          >
            <div className=" min-w-20 flex flex-row gap-4 text-wshadow_80 font-medium text-h2  rounded-xl ">
              Génération en cours ...
            </div>{" "}
            <SexySpin fillColor="border-green-400"></SexySpin>
            <div className="  text-wshadow_50  text-h4">{secondsPassed}s</div>
          </div>
        </>
      )}
    </div>
  );
};

export default NewAttestationModal;
